"use client";

import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlay,
    faPause,
    faMusic,
    faHeadphones,
} from "@fortawesome/free-solid-svg-icons";
import { cn } from "@pulsex/utils";
import { Post } from "@pulsex/types";
import pulseConfig from "@/pulsex.config";

const AudioPlayer = ({ post }: { post: Post }) => {
    const audioMedia = post?.findMedia(
        (media: any) => media?.type === "audio"
    );

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const togglePlay = () => {
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.play();
            setIsPlaying(true);
        } else if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };
    useEffect(() => {
        const updateTime = () => {
            const currentAudio = audioRef.current;
            if (currentAudio) {
                setCurrentTime(currentAudio.currentTime);
                setDuration(currentAudio.duration);
            }
        };

        const currentAudio = audioRef.current;
        if (currentAudio) {
            currentAudio.addEventListener("timeupdate", updateTime);
            currentAudio.addEventListener("loadedmetadata", updateTime);
            return () => {
                currentAudio.removeEventListener("timeupdate", updateTime);
                currentAudio.removeEventListener("loadedmetadata", updateTime);
            };
        }
    }, []);

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const [wantsToListen, setWantsToListen] = useState(false);

    const progress = (currentTime / duration) * 96 || 0;
    if (!audioMedia) return "";
    return (
        <div className="flex items-center">
            <div
                className={cn(
                    "flex items-center",
                    !wantsToListen && "cursor-pointer"
                )}
                onClick={() => {
                    if (!wantsToListen) {
                        setWantsToListen(true);
                        togglePlay();
                    }
                }}
            >
                <div className="w-11 h-11 bg-black rounded-full flex items-center justify-center text-white text-lg">
                    <FontAwesomeIcon icon={faHeadphones} size="lg" />
                </div>
                <span className="text-black text-[15px] font-bold ml-2 dark:text-white">
                    Listen
                    {audioRef.current?.duration && (
                        <span className="font-normal text-black text-[11px] ml-2 dark:text-white opacity-50">
                            {formatTime(audioRef.current?.duration)}min
                        </span>
                    )}
                </span>
            </div>
            <audio
                ref={audioRef}
                src={pulseConfig.MEDIA_URL + "/" + audioMedia?.url}
            />

            {wantsToListen && (
                <>
                    <div className="flex-1">
                        <div className="flex items-center space-x-2">
                            <button
                                className="hover:bg-blue-700 text-black font-bold py-2 px-4 rounded dark:text-white"
                                onClick={togglePlay}
                            >
                                <FontAwesomeIcon
                                    icon={isPlaying ? faPause : faPlay}
                                />
                            </button>

                            <div className="relative">
                                <div
                                    className={cn(
                                        "text-xs absolute -top-7 -translate-x-1",
                                        isPlaying ? "" : "hidden"
                                    )}
                                    style={{ left: `${progress}%` }}
                                >
                                    {formatTime(currentTime)}
                                </div>
                                <input
                                    ref={inputRef}
                                    type="range"
                                    min={0}
                                    max={duration}
                                    value={currentTime}
                                    className={cn(
                                        "block w-80 h-1 accent-black  dark:accent-white border-none bg-gray-400 cursor-pointer",
                                        !isPlaying && "thumb-hidden",
                                        "lg:w-full"
                                    )}
                                    onChange={(e) => {
                                        const time = Number(e.target.value);
                                        setCurrentTime(time);
                                        if (audioRef.current) {
                                            audioRef.current.currentTime = time;
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AudioPlayer;
